import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  FolderKanban, 
  CreditCard, 
  Mail, 
  Settings,
  ChevronDown,
  ChevronRight,
  Building2,
  Briefcase,
  DollarSign,
  BarChart3,
  Menu,
  X
} from 'lucide-react';

const AdminSidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({
    blog: true,
    clients: true,
    projects: false,
    payments: false
  });

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const menuItems = [
    {
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin/dashboard',
      single: true
    },
    {
      title: 'Blog Management',
      icon: FileText,
      key: 'blog',
      submenu: [
        { title: 'All Posts', path: '/admin/posts', icon: FileText },
        { title: 'Add New Post', path: '/admin/posts/new', icon: FileText },
        { title: 'Categories', path: '/admin/categories', icon: FolderKanban }
      ]
    },
    {
      title: 'Client Management',
      icon: Users,
      key: 'clients',
      submenu: [
        { title: 'All Clients', path: '/admin/clients', icon: Building2 },
        { title: 'Add New Client', path: '/admin/clients/new', icon: Users },
        { title: 'Client Statistics', path: '/admin/clients/stats', icon: BarChart3 }
      ]
    },
    {
      title: 'Project Management',
      icon: Briefcase,
      key: 'projects',
      submenu: [
        { title: 'All Projects', path: '/admin/projects', icon: FolderKanban },
        { title: 'Add New Project', path: '/admin/projects/new', icon: Briefcase },
        { title: 'Project Progress', path: '/admin/projects/progress', icon: BarChart3 }
      ]
    },
    {
      title: 'Payment Management',
      icon: CreditCard,
      key: 'payments',
      submenu: [
        { title: 'All Payments', path: '/admin/payments', icon: DollarSign },
        { title: 'Add Payment', path: '/admin/payments/new', icon: CreditCard },
        { title: 'Payment Reports', path: '/admin/payments/reports', icon: BarChart3 },
        { title: 'Pending Payments', path: '/admin/payments/pending', icon: CreditCard }
      ]
    },
    {
      title: 'Contact Enquiries',
      icon: Mail,
      path: '/admin/contacts',
      single: true
    },
    {
      title: 'Settings',
      icon: Settings,
      path: '/admin/settings',
      single: true
    }
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-primary text-white z-50 transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-64
      `}>
        <div className="h-full flex flex-col">
        {/* Logo & Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <Link to="/admin/dashboard" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gold rounded-lg flex items-center justify-center">
              <span className="text-primary font-bold text-lg">KDS</span>
            </div>
            <div>
              <div className="font-bold text-sm">Karni Digital</div>
              <div className="text-xs text-gold">Admin Panel</div>
            </div>
          </Link>
          <button 
            onClick={() => setIsOpen(false)}
            className="lg:hidden text-white hover:text-gold"
          >
            <X size={24} />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {menuItems.map((item, index) => (
            <div key={index}>
              {item.single ? (
                // Single Menu Item
                <Link
                  to={item.path}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg transition-all border-l-4
                    ${isActive(item.path) 
                      ? 'bg-gold text-primary font-semibold border-gold' 
                      : 'hover:bg-white/10 text-white border-transparent'
                    }
                  `}
                >
                  <item.icon size={20} />
                  <span>{item.title}</span>
                </Link>
              ) : (
                // Menu with Submenu
                <div>
                  <button
                    onClick={() => toggleMenu(item.key)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-white/10 transition-all text-white"
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon size={20} />
                      <span className="font-medium">{item.title}</span>
                    </div>
                    {expandedMenus[item.key] ? (
                      <ChevronDown size={18} />
                    ) : (
                      <ChevronRight size={18} />
                    )}
                  </button>
                  
                  {/* Submenu */}
                  {expandedMenus[item.key] && (
                    <div className="ml-1 mt-1 space-y-1">
                      {item.submenu.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.path}
                          className={`
                            flex items-center space-x-3 px-4 py-2.5 rounded-md transition-all text-sm border-l-4
                            ${isActive(subItem.path)
                              ? 'bg-white/10 text-gold font-semibold border-gold'
                              : 'hover:bg-white/5 text-gray-200 border-transparent'
                            }
                          `}
                        >
                          <subItem.icon size={16} />
                          <span>{subItem.title}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </nav>
        {/* User Info at Bottom */}
        <div className="mt-auto p-4 border-t border-white/10 bg-primary/95 sticky bottom-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold">A</span>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">Admin User</div>
              <div className="text-xs text-gray-300 truncate">Super Admin</div>
            </div>
          </div>
        </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
